import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
// Dialog removed - video playback disabled
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, 
  Users, 
  Award, 
  FileText, 
  Video, 
  Download,
  Eye,
  Calendar,
  Mail,
  CheckCircle,
  XCircle,
  Shield,
  ShieldCheck
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResultsAnalysis = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    fetchAnalysis();
  }, []);

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/teacher/results-analysis`);
      setData(response.data);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (submissionId, approve) => {
    try {
      await axios.put(`${API}/teacher/submissions/${submissionId}/approve?approve=${approve}`);
      toast.success(approve ? t('resultApproved') : t('approvalRevoked'));
      fetchAnalysis(); // Refresh data
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const downloadVideo = async (recordingId, type) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API}/teacher/download-video/${recordingId}/${type}`,
        { 
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const blob = new Blob([response.data], { type: 'video/webm' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `exam_${type}_${recordingId}.webm`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success(t('downloadVideo'));
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('error'));
    }
  };

  // Removed video playback - download only

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!data) {
    return <div className="text-center text-gray-500">{t('noData')}</div>;
  }

  const { 
    total_submissions, 
    average_score, 
    score_distribution, 
    total_students,
    students_summary,
    submissions 
  } = data;

  return (
    <div className="space-y-6" data-testid="results-analysis">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900">{t('resultsAnalysis')}</h2>
        <p className="text-gray-600 mt-1">{t('detailedResults')}</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="shadow-lg border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {t('totalSubmissions')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-indigo-600">{total_submissions}</div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              {t('averageScore')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-green-600">{average_score}%</div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Users className="h-4 w-4" />
              {t('totalStudents')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-cyan-600">{total_students}</div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Award className="h-4 w-4" />
              {t('excellent')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-yellow-600">{score_distribution.excellent}</div>
          </CardContent>
        </Card>
      </div>

      {/* Score Distribution */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle>{t('scoreDistribution')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { key: 'excellent', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-100' },
              { key: 'good', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-100' },
              { key: 'average', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgLight: 'bg-yellow-100' },
              { key: 'poor', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-100' }
            ].map(({ key, color, textColor, bgLight }) => {
              const count = score_distribution[key];
              const percentage = total_submissions > 0 ? (count / total_submissions) * 100 : 0;
              
              return (
                <div key={key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${textColor}`}>{t(key)}</span>
                    <span className="text-sm text-gray-600">
                      {count} ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                  <div className={`h-4 ${bgLight} rounded-full overflow-hidden`}>
                    <div
                      className={`h-full ${color} transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Student Performance Summary */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle>{t('studentPerformance')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {students_summary.map((student, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg">
                    {student.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{student.name}</p>
                    <p className="text-sm text-gray-600">{student.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold ${
                    student.average_score >= 70 ? 'text-green-600' :
                    student.average_score >= 50 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {student.average_score.toFixed(1)}%
                  </div>
                  <p className="text-sm text-gray-600">
                    {student.exams_taken} {t('examsTaken')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Detailed Submissions */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle>{t('detailedResults')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {submissions.map((submission, idx) => (
              <Card key={idx} className="border-2 border-gray-200">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Student & Exam Info */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-gray-500" />
                        <span className="font-semibold">{submission.student_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">{submission.student_email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">{submission.exam_title}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">
                          {new Date(submission.submitted_at).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {/* Score & Approval */}
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="text-center">
                        <div className={`text-5xl font-bold ${
                          submission.score >= 70 ? 'text-green-600' :
                          submission.score >= 50 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {submission.score}%
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                          {submission.total_questions} {t('questionsCount')}
                        </p>
                        {submission.score >= 70 ? (
                          <CheckCircle className="h-8 w-8 text-green-500 mx-auto mt-2" />
                        ) : (
                          <XCircle className="h-8 w-8 text-red-500 mx-auto mt-2" />
                        )}
                      </div>
                      
                      {/* Approval Status & Button */}
                      <div className="w-full space-y-2">
                        <Badge 
                          variant={submission.approved ? "default" : "secondary"}
                          className={`w-full justify-center ${
                            submission.approved 
                              ? 'bg-green-100 text-green-700 border-green-300' 
                              : 'bg-yellow-100 text-yellow-700 border-yellow-300'
                          }`}
                        >
                          {submission.approved ? (
                            <>
                              <ShieldCheck className="h-3 w-3 mr-1" />
                              {t('approved')}
                            </>
                          ) : (
                            <>
                              <Shield className="h-3 w-3 mr-1" />
                              {t('pending')}
                            </>
                          )}
                        </Badge>
                        
                        {submission.approved ? (
                          <Button
                            onClick={() => handleApproval(submission.id, false)}
                            variant="outline"
                            className="w-full border-red-300 text-red-600 hover:bg-red-50"
                            size="sm"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            {t('revokeApproval')}
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleApproval(submission.id, true)}
                            className="w-full bg-gradient-to-r from-green-600 to-green-700"
                            size="sm"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {t('approveResult')}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Videos */}
                    <div className="space-y-2">
                      {submission.has_camera_video && (
                        <Button
                          onClick={() => downloadVideo(submission.recording_id, 'camera')}
                          variant="outline"
                          className="w-full"
                          size="sm"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          {t('downloadCameraVideo')}
                        </Button>
                      )}

                      {submission.has_screen_video && (
                        <Button
                          onClick={() => downloadVideo(submission.recording_id, 'screen')}
                          variant="outline"
                          className="w-full"
                          size="sm"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          {t('downloadScreenVideo')}
                        </Button>
                      )}

                      {!submission.has_camera_video && !submission.has_screen_video && (
                        <p className="text-sm text-gray-500 text-center py-4">
                          {t('noRecordings')}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Video dialog removed - download only */}
    </div>
  );
};

export default ResultsAnalysis;
